import React, { useState, useEffect } from 'react';
import api, { formatCurrency, settingsAPI } from '../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PartyAutocomplete from '../components/PartyAutocomplete';

export default function AdvanceForm() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState({
    type: 'received', party_name: '', amount: '', date: new Date().toISOString().split('T')[0],
    reference: '', bank_account: 'yes_bank',
  });

  useEffect(() => { settingsAPI.bankAccounts().then(r => setBanks(r.data || [])).catch(() => {}); }, []);

  const u = (f, v) => setForm(s => ({ ...s, [f]: v }));
  const isReceived = form.type === 'received';

  const handleSave = async () => {
    if (!form.party_name) { toast.error('Select a party'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter valid amount'); return; }
    setSaving(true);
    try {
      const res = await api.post('/advances', { ...form, amount: parseFloat(form.amount) });
      toast.success(`${isReceived ? 'Advance Receipt' : 'Advance Payment'} ${res.data.voucher_number} created`);
      navigate('/advances');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="advance-form-page" className="max-w-2xl mx-auto space-y-5">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-md"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
        <div><h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">{isReceived ? 'New Advance Receipt' : 'New Advance Payment'}</h1>
          <p className="text-sm text-slate-500 font-body">{isReceived ? 'Record advance received from customer' : 'Record advance paid to vendor'}</p></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
        <div className="flex items-center gap-3" data-testid="advance-type-selector">
          <span className="text-sm font-semibold text-slate-700">Type:</span>
          <button onClick={() => u('type', 'received')} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${isReceived ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="type-received">Advance Receipt</button>
          <button onClick={() => u('type', 'paid')} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${!isReceived ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="type-paid">Advance Payment</button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="form-label">{isReceived ? 'Customer Name' : 'Vendor Name'}</label>
            <PartyAutocomplete value={form.party_name} onChange={v => u('party_name', v)} partyType={isReceived ? 'customer' : 'vendor'} placeholder={isReceived ? 'Search customer...' : 'Search vendor...'} />
          </div>
          <div><label className="form-label">Amount (Rs.)</label><input type="number" value={form.amount} onChange={e => u('amount', e.target.value)} className="form-input font-mono text-right text-lg" placeholder="0.00" min="0" data-testid="advance-amount" /></div>
          <div><label className="form-label">Date</label><input type="date" value={form.date} onChange={e => u('date', e.target.value)} className="form-input" data-testid="advance-date" /></div>
          <div><label className="form-label">Reference / Narration</label><input type="text" value={form.reference} onChange={e => u('reference', e.target.value)} className="form-input" placeholder="e.g. Advance for March freight" /></div>
          <div><label className="form-label">Bank Account</label>
            <select value={form.bank_account} onChange={e => u('bank_account', e.target.value)} className="form-input">
              <option value="cash">Cash</option>
              {banks.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
          </div>
        </div>

        {form.amount && parseFloat(form.amount) > 0 && (
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
            <div className="flex items-center gap-2 mb-2"><Wallet className="w-4 h-4 text-slate-500" /><span className="text-sm font-semibold text-slate-700">Entry Preview</span></div>
            {isReceived ? (
              <div className="text-xs text-slate-600 space-y-1 font-mono">
                <p>Bank / Cash <span className="text-emerald-700 font-bold">Dr</span> &nbsp; Rs. {formatCurrency(parseFloat(form.amount))}</p>
                <p>&nbsp;&nbsp;&nbsp;To Customer Advances <span className="text-rose-700 font-bold">Cr</span> &nbsp; Rs. {formatCurrency(parseFloat(form.amount))}</p>
              </div>
            ) : (
              <div className="text-xs text-slate-600 space-y-1 font-mono">
                <p>Vendor Advances <span className="text-emerald-700 font-bold">Dr</span> &nbsp; Rs. {formatCurrency(parseFloat(form.amount))}</p>
                <p>&nbsp;&nbsp;&nbsp;To Bank / Cash <span className="text-rose-700 font-bold">Cr</span> &nbsp; Rs. {formatCurrency(parseFloat(form.amount))}</p>
              </div>
            )}
          </div>
        )}

        <button onClick={handleSave} disabled={saving} className="btn-accent w-full flex items-center justify-center gap-2" data-testid="save-advance-btn">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : `Save ${isReceived ? 'Advance Receipt' : 'Advance Payment'}`}
        </button>
      </div>
    </div>
  );
}
